import { Link } from 'react-router-dom';
import { Check, Shield, FileCheck, AlertCircle } from 'lucide-react';
import { Layout } from '../components/layout/Layout';

export default function Policy() {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-slate-900 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <Shield className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h1 className="font-heading text-4xl md:text-5xl font-bold mb-6">
              Our Policies
            </h1>
            <p className="text-lg text-slate-300">
              Transparency and trust are the foundation of our services. 
              Review our policies below.
            </p>
          </div>
        </div>
      </section>

      {/* Money Back Guarantee */}
      <section className="py-24 bg-slate-800 text-white" data-testid="refund-policy">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold mb-4">
              Money Back Guarantee
            </h2>
          </div>
          <div className="text-left bg-slate-700 rounded-xl p-8">
            <div className="bg-gradient-to-r from-amber-500/20 to-teal-500/20 border border-amber-500/30 rounded-lg p-6 mb-6">
              <h3 className="text-xl font-bold text-amber-400 mb-2 text-center">90-Day Results Guarantee</h3>
              <p className="text-center text-slate-200">
                If no results are achieved within 90 days of service, you are entitled to a <span className="text-amber-400 font-bold">full refund</span>.
              </p>
            </div>

            {/* What "No Results" Means */}
            <div className="mb-6 p-4 bg-slate-600/50 rounded-lg border border-slate-500">
              <h4 className="font-semibold text-white mb-3">"No Results" Definition:</h4>
              <p className="text-sm text-slate-300 mb-3">
                The 90-day money back guarantee applies only if <strong className="text-amber-400">none</strong> of the following negative items have been removed from your credit report:
              </p>
              <ul className="grid grid-cols-2 gap-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-teal-400 flex-shrink-0" />
                  <span>Inquiries</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-teal-400 flex-shrink-0" />
                  <span>Personal Information Errors</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-teal-400 flex-shrink-0" />
                  <span>Collections</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-teal-400 flex-shrink-0" />
                  <span>Charge-Offs</span>
                </li>
              </ul>
              <p className="text-xs text-slate-400 mt-3">
                If any of these items are successfully removed or corrected, the guarantee is considered fulfilled.
              </p>
            </div>
            
            <h4 className="font-semibold text-white mb-4">Policy Terms & Conditions:</h4>
            <ul className="space-y-4 text-slate-300">
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-400 mt-1 flex-shrink-0" />
                <span>Clients may cancel services at any time</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-400 mt-1 flex-shrink-0" />
                <span>Full refund if no results within 90 days of active service</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-teal-400 mt-1 flex-shrink-0" />
                <span>All billing practices adhere to CROA requirements</span>
              </li>
            </ul>

            {/* Conditions That Void the Guarantee */}
            <div className="mt-6 pt-6 border-t border-slate-600">
              <h4 className="font-semibold text-red-400 mb-3 flex items-center gap-2">
                <AlertCircle className="h-5 w-5" />
                Conditions That Void the Guarantee:
              </h4>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex items-start gap-3">
                  <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">New Negative Items:</strong> If any new negative items (collections, late payments, inquiries, etc.) are added to your credit report during the 90-day period, the money back guarantee is <strong className="text-red-400">voided</strong>.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Cancellation:</strong> Canceling your services at any time does <strong className="text-red-400">NOT</strong> guarantee a refund. The money back guarantee only applies if no results are achieved after 90 days of active, continuous service.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Policy Abuse:</strong> This guarantee is intended for clients who actively participate in the restoration process. Policy abuse or fraud will void the guarantee.</span>
                </li>
              </ul>
            </div>

            {/* Required Documents Section */}
            <div className="mt-6 pt-6 border-t border-slate-600">
              <h4 className="font-semibold text-teal-400 mb-3 flex items-center gap-2">
                <FileCheck className="h-5 w-5" />
                Required Documentation
              </h4>
              <p className="text-sm text-slate-300 mb-4">
                To ensure the credit reporting agencies can verify your identity and process disputes efficiently, 
                all clients are required to submit the following documents:
              </p>
              <ul className="space-y-3 text-sm text-slate-300">
                <li className="flex items-start gap-3">
                  <Check className="h-4 w-4 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Valid Photo ID</strong> — A current driver's license or government-issued identification card</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-4 w-4 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Proof of Residence</strong> — A recent utility bill, bank statement, or similar official document showing your current address</span>
                </li>
                <li className="flex items-start gap-3">
                  <Check className="h-4 w-4 text-teal-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Social Security Verification</strong> — Your Social Security card, or if unavailable, the last 4 digits of your SSN</span>
                </li>
              </ul>
              <p className="mt-4 text-amber-400 text-sm font-medium">
                All documents must be clear, legible, and fully visible to ensure timely processing of your case.
              </p>
            </div>
            
            <p className="mt-6 text-sm text-amber-400 border-t border-slate-600 pt-6">
              <strong>Disclaimer:</strong> We do not guarantee specific credit score increases 
              or promise the removal of all negative items. Results vary depending on the 
              individual credit profile and the response of creditors or credit bureaus.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Section */}
      <section className="py-24 bg-slate-50" data-testid="privacy-policy">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Privacy Policy
            </h2>
            <p className="text-slate-600">
              Your privacy and security are our top priorities. Here's how we protect your information.
            </p>
          </div>
          
          <div className="bg-white rounded-xl p-8 shadow-lg border border-slate-200">
            <div className="space-y-6">
              <div>
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-teal-600" />
                  Information We Collect
                </h4>
                <p className="text-slate-600 text-sm">
                  To provide our credit restoration services, we collect the following personal information:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Personal Identification:</strong> Full name, date of birth, and contact information</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Valid Photo ID:</strong> Current driver's license or government-issued ID for identity verification</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Proof of Residence:</strong> Utility bills, bank statements, or similar documents to verify your address</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Social Security Information:</strong> Your SSN card or last 4 digits (if card is unavailable) for credit bureau verification</span>
                  </li>
                </ul>
              </div>

              <div className="pt-6 border-t border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <FileCheck className="h-5 w-5 text-teal-600" />
                  Document Requirements
                </h4>
                <p className="text-slate-600 text-sm mb-3">
                  All submitted documents must meet the following criteria to ensure proper processing:
                </p>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>Documents must be <strong>current and valid</strong> (not expired)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>All text and images must be <strong>clear, legible, and fully visible</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>Documents should be <strong>high-quality scans or photos</strong> (no blurry images)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <span>All four corners of each document must be visible</span>
                  </li>
                </ul>
              </div>

              <div className="pt-6 border-t border-slate-200">
                <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                  <Shield className="h-5 w-5 text-teal-600" />
                  How We Protect Your Information
                </h4>
                <ul className="space-y-2 text-sm text-slate-600">
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>All data is encrypted using industry-standard SSL/TLS protocols</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Your information is never sold or shared with third parties for marketing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>Documents are stored securely and accessed only by authorized personnel</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="h-4 w-4 text-teal-600 mt-0.5 flex-shrink-0" />
                    <span>We comply with all FCRA, CROA, and data protection regulations</span>
                  </li>
                </ul>
              </div>

              <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Why We Need These Documents:</strong> The credit reporting agencies (Equifax, Experian, and TransUnion) 
                  require identity verification before processing any dispute requests. By providing clear, complete documentation, 
                  you help us work more efficiently on your behalf and avoid delays in your credit restoration process.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-slate-600 mb-6">
            View our packages and begin your credit restoration journey today.
          </p>
          <Link to="/pricing">
            <button className="bg-amber-600 hover:bg-amber-700 text-white font-medium px-8 py-3 rounded-lg transition-colors">
              View Packages
            </button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
